import { fetchAuthSession } from "@aws-amplify/core";

/**
 * Generic call into qboProxy.
 * - operation: name in qboProxy's ops map
 * - payload:   operation-specific payload
 * - qbenv:     "production" | "sandbox"
 * - app:       "shared" | "legacy"
 */
export async function callQboProxy({
  operation,
  payload = {},
  qbenv,
  app = "shared",
  id,
}) {
  if (!operation) {
    throw new Error("operation is required");
  }
  if (!qbenv) {
    const err = new Error("qbenv is required");
    err.code = "MissingEnv";
    throw err;
  }

  const body = {
    operation,
    app,
    qbenv,
    ...(id ? { id } : {}),
    payload,
  };

  const session = await fetchAuthSession();
  const authToken = `Bearer ${session.tokens.idToken}`;

  const API_ENDPOINT = `${import.meta.env.VITE_API_BASE_URL}/qboproxy`;

  const res = await fetch(API_ENDPOINT,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: authToken,
      },
      body: JSON.stringify(body),
    }
  );

  let parsed;
  try {
    parsed = await res.json();
  } catch {
    parsed = null;
  }

  console.log('callQboProxy parsed after unwrap:', operation, typeof parsed, Array.isArray(parsed), parsed);
  
  // qboProxy returns Lambda proxy format {statusCode, headers, body} — unwrap it
  if (parsed && typeof parsed.statusCode === 'number' && parsed.body !== undefined) {
    const innerStatus = parsed.statusCode;
    try {
      parsed = typeof parsed.body === 'string' ? JSON.parse(parsed.body) : parsed.body;
    } catch {
      parsed = null;
    }

    if (innerStatus === 401 || innerStatus === 412) {
      const err = new Error("InvalidToken");
      err.code = "InvalidToken";
      err.httpStatus = innerStatus;
      err.raw = parsed;
      throw err;
    }

    if (innerStatus >= 400) {
      const msg = (parsed && parsed.error) || `HTTP_${innerStatus}`;
      const err = new Error(msg);
      err.code = "ProxyError";
      err.httpStatus = innerStatus;
      err.raw = parsed;
      throw err;
    }

    return parsed;
  }

  // Normal flow for non-proxy responses
  if (res.status === 401 || res.status === 412) {
    const err = new Error("InvalidToken");
    err.code = "InvalidToken";
    err.httpStatus = res.status;
    err.raw = parsed;
    throw err;
  }

  if (!res.ok) {
    const msg = (parsed && parsed.error) || `HTTP_${res.status}`;
    const err = new Error(msg);
    err.code = "ProxyError";
    err.httpStatus = res.status;
    err.raw = parsed;
    throw err;
  }

  return parsed;
}

// ---------- Customers ----------

// Single customer by Id
export async function getCustomerViaProxy({
  qbenv,
  app = "shared",
  customerId,
  id,
}) {
  if (!customerId) {
    throw new Error("customerId is required");
  }

  return callQboProxy({
    operation: "getCustomer",
    qbenv,
    app,
    id,
    payload: { customerId },
  });
}

/**
 * Customers list/search
 * Returns an array of Customer objects (same shape as before).
 */
export async function getCustomersViaProxy({
  qbenv,
  app = "shared",
  id,
  customerName,
} = {}) {
  const data = await callQboProxy({
    operation: "getCustomers",
    qbenv,
    app,
    id,
    payload: {
      customerName: customerName || undefined,
    },
  });
  return Array.isArray(data) ? data : [];
}

// ---------- Classes ----------

/**
 * Classes
 */
export async function getClassesViaProxy({
  qbenv,
  app = "shared",
  id,
} = {}) {
  const data = await callQboProxy({
    operation: "getClasses",
    qbenv,
    app,
    id,
  });
  return Array.isArray(data) ? data : [];
}

// ---------- Terms ----------

export async function getTermsViaProxy({
  qbenv,
  app = "shared",
  id,
  minorVersion,
} = {}) {
  const data = await callQboProxy({
    operation: "getTerms",
    qbenv,
    app,
    id,
    payload: {
      minorVersion,
    },
  });
  return Array.isArray(data) ? data : [];
}

// ---------- Items ----------

export async function getItemsViaProxy({
  qbenv,
  app = "shared",
  id,
  minorVersion,
} = {}) {
  const data = await callQboProxy({
    operation: "getItems",
    qbenv,
    app,
    id,
    payload: {
      minorVersion,
    },
  });
  return Array.isArray(data) ? data : [];
}

// ---------- Vendors ----------

export async function getVendorsViaProxy({
  qbenv,
  app = "shared",
  id,
  startPosition,
  maxResults,
  minorVersion,
} = {}) {
  const data = await callQboProxy({
    operation: "getVendors",
    qbenv,
    app,
    id,
    payload: {
      startPosition,
      maxResults,
      minorVersion,
    },
  });
  return Array.isArray(data) ? data : [];
}

/**
 * Single vendor by Id — authoritative active/deleted status from QBO.
 * Returns { found, active, vendorId, displayName }.
 * Used by the pre-publish guard to avoid sending a doomed transaction
 * referencing a deactivated vendor (which QBO rejects as a 400 -> 500).
 */
export async function getVendorViaProxy({
  qbenv,
  app = "shared",
  id,
  vendorId,
  minorVersion,
} = {}) {
  if (!vendorId) {
    throw new Error("vendorId is required");
  }

  return callQboProxy({
    operation: "getVendor",
    qbenv,
    app,
    id,
    payload: {
      vendorId: String(vendorId),
      minorVersion,
    },
  });
}

// ---------- Chart of Accounts / Expense Categories ----------

export async function getChartOfAccountsViaProxy({
  qbenv,
  app = "shared",
  id,
  minorVersion,
} = {}) {
  const data = await callQboProxy({
    operation: "getChartOfAccounts",
    qbenv,
    app,
    id,
    payload: {
      minorVersion,
    },
  });
  return Array.isArray(data) ? data : [];
}

export async function createAccountViaProxy({
  qbenv,
  app = "shared",
  Name,
  AccountType,
  AccountSubType,
  Description,
  Active,
} = {}) {
  const data = await callQboProxy({
    operation: "createAccount",
    qbenv,
    app,
    payload: {
      Name,
      AccountType,
      AccountSubType,
      Description,
      Active,
    },
  });
  return data;
}

export async function createClassViaProxy({
  qbenv,
  app = "shared",
  Name,
  Description,
  Active,
} = {}) {
  const data = await callQboProxy({
    operation: "createClass",
    qbenv,
    app,
    payload: {
      Name,
      Description,
      Active,
    },
  });
  return data;
}

export async function getExpenseCategoriesViaProxy({
  qbenv,
  app = "shared",
  id,
  minorVersion,
} = {}) {
  const data = await callQboProxy({
    operation: "getExpenseCategories",
    qbenv,
    app,
    id,
    payload: {
      minorVersion,
    },
  });
  return Array.isArray(data) ? data : [];
}

// ---------- “VERIFY TAB” HELPERS (today’s txns) ----------

/**
 * Today’s Purchases (for Verify tab)
 * payload: { txnDate? } – if omitted, backend can default to "today"
 */
export async function getTodaysPurchasesViaProxy({
  qbenv,
  app = "shared",
  id,
  txnDate,
} = {}) {
  const data = await callQboProxy({
    operation: "getTodaysPurchases",
    qbenv,
    app,
    id,
    payload: { txnDate },
  });
  return Array.isArray(data) ? data : [];
}

/**
 * Today’s Bills (for Verify tab)
 */
export async function getTodaysBillsViaProxy({
  qbenv,
  app = "shared",
  id,
  txnDate,
} = {}) {
  const data = await callQboProxy({
    operation: "getTodaysBills",
    qbenv,
    app,
    id,
    payload: { txnDate },
  });
  return Array.isArray(data) ? data : [];
}

// ---------- “VERIFY TAB” HELPERS (rolling window) ----------

/**
 * Purchases CREATED in the last N hours (Verify tab).
 *
 * Supersedes getTodaysPurchasesViaProxy. Sends an hour count instead of a
 * calendar date so the backend owns the window boundaries — no UTC date
 * truncation on the client, no timezone ambiguity in the QBO query literal.
 */
export async function getRecentPurchasesViaProxy({
  qbenv,
  app = "shared",
  id,
  hours = 24,
} = {}) {
  const data = await callQboProxy({
    operation: "getRecentPurchases",
    qbenv,
    app,
    id,
    payload: { hours },
  });
  return Array.isArray(data) ? data : [];
}

/**
 * Bills CREATED in the last N hours (Verify tab).
 */
export async function getRecentBillsViaProxy({
  qbenv,
  app = "shared",
  id,
  hours = 24,
} = {}) {
  const data = await callQboProxy({
    operation: "getRecentBills",
    qbenv,
    app,
    id,
    payload: { hours },
  });
  return Array.isArray(data) ? data : [];
}

// ---------- Invoices ----------

export async function getRecentInvoicesViaProxy({
  qbenv,
  app = "shared",
  id,
  customerId,
  minorVersion,
} = {}) {
  if (!customerId) {
    throw new Error("customerId is required");
  }

  const data = await callQboProxy({
    operation: "getRecentInvoices",
    qbenv,
    app,
    id,
    payload: {
      customerId,
      minorVersion,
    },
  });

  return Array.isArray(data) ? data : [];
}

// ---------- Attachable / Attachables ----------

export async function getAttachableByIdViaProxy({
  qbenv,
  app = "shared",
  id,
  attachableId,
  minorVersion,
} = {}) {
  if (!attachableId) {
    throw new Error("attachableId is required");
  }

  const data = await callQboProxy({
    operation: "getAttachableById",
    qbenv,
    app,
    id,
    payload: {
      attachableId,
      minorVersion,
    },
  });

  return data;
}

export async function queryAttachablesViaProxy({
  qbenv,
  app = "shared",
  id,
  date,
  company,
  minorVersion,
} = {}) {
  if (!date || !company) {
    throw new Error("date and company are required");
  }

  const data = await callQboProxy({
    operation: "queryAttachables",
    qbenv,
    app,
    id,
    payload: {
      date,
      company,
      minorVersion,
    },
  });

  return Array.isArray(data) ? data : [];
}

// ---------- File upload / Vendor create ----------

export async function pushFileToQuickBooksViaProxy({
  qbenv,
  app = "shared",
  id,
  filename,
  minorVersion,
} = {}) {
  if (!filename) {
    throw new Error("filename is required");
  }

  const data = await callQboProxy({
    operation: "pushFileToQuickBooks",
    qbenv,
    app,
    id,
    payload: {
      filename,
      minorVersion,
    },
  });

  return data; // { raw, attachable: { Id, FileName } }
}

export async function createVendorViaProxy({
  qbenv,
  app = "shared",
  id,
  vendorName,
} = {}) {
  if (!vendorName) {
    throw new Error("vendorName is required");
  }

  const data = await callQboProxy({
    operation: "createVendor",
    qbenv,
    app,
    id,
    payload: {
      vendorName,
    },
  });

  return data;
}

// ---------- Company info ----------

/**
 * Company info, but with the same { valid, error } contract
 * your old getCompanyInfo() used, so existing logic can be similar.
 */
export async function getCompanyInfoViaProxy({
  qbenv,
  app = "shared",
  id,
} = {}) {
  try {
    const companyInfo = await callQboProxy({
      operation: "getCompanyInfo",
      qbenv,
      app,
      id,
      payload: {},
    });
    return { valid: !!companyInfo, error: null, companyInfo };
  } catch (err) {
    if (err.code === "InvalidToken") {
      return { valid: false, error: "InvalidToken" };
    }
    return { valid: false, error: err.message || "ProxyError" };
  }
}

// ---------- Meta / connection status ----------

export async function getQboConnectionMeta({
  qbenv,
  app = "shared",
  id,
} = {}) {
  return callQboProxy({
    operation: "getQboConnectionMeta",
    qbenv,
    app,
    id,
  });
}

// ---------- Raw token helper (if ever needed) ----------

export async function getAccessTokenViaProxy({ qbenv, app = "shared", id }) {
  if (!qbenv) {
    const err = new Error("qbenv is required for getAccessTokenViaProxy");
    err.code = "MissingEnv";
    throw err;
  }

  const res = await callQboProxy({
    operation: "getAccessToken",
    qbenv,
    app,
    payload: { id },
  });

  return res;
}

/**
 * Upload an attachment from S3 into QuickBooks via qboProxy.
 * This wraps the uploadAttachmentFromS3 op.
 *
 * Expects:
 * - identityId: Cognito identityId
 * - key:        S3 key (the "341c90bd-...:DJT-..." part)
 * - filename:   Logical filename to use in QBO (e.g. "DJT-2025-11-22-XXXX.pdf")
 * - filetype:   MIME type (defaults to application/pdf)
 * - bucket:     S3 bucket (defaults to qb-invoice-attachments-prod)
 */
export async function uploadAttachmentFromS3ViaProxy({
  qbenv,
  bucket,
  key,          // full S3 key: "protected/<identityId>/<uuid>:<filename>.pdf"
  identityId,   // 🔹 REQUIRED
  fileName,
  fileType = "application/pdf",
  app = "shared",
  id,
} = {}) {
  console.log("uploadAttachmentFromS3ViaProxy payload (raw args):", {
    bucket,
    key,
    identityId,
    fileName,
    fileType,
    qbenv,
    app,
    id,
  });

  if (!bucket || !key || !fileName || !fileType || !qbenv) {
    const err = new Error("Missing required uploadAttachmentFromS3 params");
    err.details = { bucket, key, identityId, fileName, fileType, qbenv };
    console.error(err);
    throw err;
  }

  const payload = {
    bucket,
    key,
    identityId,
    fileName,
    fileType,
  };

  console.log("uploadAttachmentFromS3ViaProxy payload (to proxy):", payload);

  return callQboProxy({
    operation: "uploadAttachmentFromS3",
    payload,
    qbenv,
    app,
    id,
  });
}